import { useEffect, useMemo, useReducer, useRef } from 'react';
import { debounce, isEqual } from 'lodash';
import { createLogger } from '@/utils/createLogger';
import { screenInfoContext, ScreenInfo } from './ScreenInfoContext';
import { screenInfoReducer } from './reducer';
import {
  useUpdateEventMutation,
  UpdateEventInput,
  FullEventFragment,
} from '@/generated/graphql';

const logger = createLogger('ScreenInfoProvider');

type Props = {
  children: React.ReactNode;
  event: FullEventFragment;
};

export function ScreenInfoProvider({ children, event }: Props) {
  const [updateEvent] = useUpdateEventMutation();

  const [state, dispatch] = useReducer(screenInfoReducer, {
    name: event.name,
    date: event.date,
    location: event.location,
    description: event.description,
  });

  const debouncedOnSave = useMemo(
    () =>
      debounce((input: UpdateEventInput) => {
        logger.log('debouncedOnSave: ', {
          input,
        });
        updateEvent({
          variables: {
            eventId: event.id,
            data: input,
          },
        });
      }, 500),
    [event.id, updateEvent],
  );

  const lastSavedState = useRef(state);

  useEffect(() => {
    if (!isEqual(lastSavedState.current, state)) {
      const updateInput = screenInfoToUpdateInput(state);
      debouncedOnSave(updateInput);
      lastSavedState.current = state;
    }
  }, [state, debouncedOnSave]);

  return (
    <screenInfoContext.Provider value={{ state, dispatch }}>
      {children}
    </screenInfoContext.Provider>
  );
}

function screenInfoToUpdateInput(screenInfo: ScreenInfo): UpdateEventInput {
  return {
    name: screenInfo.name || undefined,
    date: screenInfo.date || undefined,
    location: screenInfo.location || undefined,
    description: screenInfo.description || undefined,
  };
}
